import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { getCreditPlans, getCreditPackages } from '../../../graphql/credits/queries';
import { createStripeCheckoutSession } from '../../../graphql/credits/mutations';
import Modal from '../modals/Modal';
import Button from '../Button';
import { useAlert } from 'react-alert';
import { StarIcon, RocketIcon, DiamondIcon, CrownIcon } from '../Icons';
import './UpgradeModal.scss';

const UpgradeModal = ({
  isOpen,
  onClose,
  highlightedFeature = null,
  requiredCredits = 0,
  currentBalance = 0,
  currentTier = 'FREE',
}) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    const fetchPlansAndPackages = async () => {
      try {
        const [plansResult, packagesResult] = await Promise.all([
          API.graphql(graphqlOperation(getCreditPlans)),
          API.graphql(graphqlOperation(getCreditPackages)),
        ]);

        setPlans(plansResult.data?.getCreditPlans || []);
        setPackages(packagesResult.data?.getCreditPackages || []);
      } catch (error) {
        console.error('Failed to fetch plans and packages:', error);
      }
    };

    if (isOpen) {
      fetchPlansAndPackages();
    }
  }, [isOpen]);

  const handleUpgrade = async (planId = null, packageId = null) => {
    setLoading(true);
    try {
      const { data } = await API.graphql(
        graphqlOperation(createStripeCheckoutSession, {
          planId,
          packageId,
          successUrl: `${window.location.origin}/dashboard/account/billing?success=true`,
          cancelUrl: window.location.href,
        }),
      );

      if (data.createStripeCheckoutSession.error) {
        alert.error(data.createStripeCheckoutSession.error);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = data.createStripeCheckoutSession.sessionUrl;
    } catch (error) {
      console.error('Upgrade error:', error);
      alert.error('Failed to start upgrade process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = tier => {
    switch (tier) {
      case 'STARTER':
        return '#059669';
      case 'PRO':
        return '#7c3aed';
      case 'PREMIUM':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const getTierIcon = tier => {
    switch (tier) {
      case 'STARTER':
        return <RocketIcon />;
      case 'PRO':
        return <DiamondIcon />;
      case 'PREMIUM':
        return <CrownIcon />;
      default:
        return <StarIcon />;
    }
  };
  const isCurrentTier = tier => tier === currentTier;
  const isDowngrade = tier => {
    const hierarchy = { FREE: 0, STARTER: 1, PRO: 2, PREMIUM: 3 };
    return hierarchy[tier] < hierarchy[currentTier];
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} className='upgrade-modal'>
      <div className='upgrade-modal-content'>
        <div className='modal-header'>
          <h2>{t('upgrade_your_plan')}</h2>
          {highlightedFeature && (
            <div className='feature-highlight'>
              <p>You need {requiredCredits} credits to use this feature.</p>
              <p>Current balance: {currentBalance} credits</p>
            </div>
          )}
        </div>

        <div className='tabs'>
          <button
            className={activeTab === 'subscriptions' ? 'active' : ''}
            onClick={() => setActiveTab('subscriptions')}
          >
            Monthly Plans
          </button>
          <button className={activeTab === 'credits' ? 'active' : ''} onClick={() => setActiveTab('credits')}>
            Credit Packages
          </button>
        </div>

        <div className='tab-content'>
          {activeTab === 'subscriptions' && (
            <div className='plans-grid'>
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className={`plan-card ${isCurrentTier(plan.tier) ? 'current' : ''} ${
                    isDowngrade(plan.tier) ? 'downgrade' : ''
                  }`}
                  style={{ borderColor: getTierColor(plan.tier) }}
                >
                  <div className='plan-header'>
                    <div className='plan-icon'>{getTierIcon(plan.tier)}</div>
                    <h3>{plan.displayName}</h3>
                    {isCurrentTier(plan.tier) && <span className='current-badge'>Current</span>}
                  </div>

                  <div className='plan-pricing'>
                    <span className='price'>${plan.monthlyPrice}</span>
                    <span className='period'>/month</span>
                  </div>

                  <div className='plan-credits'>
                    <strong>{plan.monthlyCredits} credits</strong> per month
                  </div>

                  <ul className='plan-features'>
                    {plan.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>

                  <Button
                    className={`plan-button ${isCurrentTier(plan.tier) ? 'current' : ''}`}
                    style={{ backgroundColor: getTierColor(plan.tier) }}
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={isCurrentTier(plan.tier) || loading}
                  >
                    {isCurrentTier(plan.tier) ? 'Current Plan' : isDowngrade(plan.tier) ? 'Downgrade' : 'Upgrade'}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'credits' && (
            <div className='packages-grid'>
              {packages.map(package_ => (
                <div key={package_.id} className='package-card'>
                  <h3>{package_.name}</h3>
                  <div className='package-pricing'>
                    <span className='price'>${package_.displayPrice}</span>
                  </div>
                  <div className='package-credits'>
                    <strong>{package_.credits}</strong> credits
                    {package_.bonusPercentage > 0 && <span className='bonus'>+{package_.bonusPercentage} bonus</span>}
                  </div>
                  <Button
                    className='package-button'
                    onClick={() => handleUpgrade(null, package_.id)}
                    disabled={loading}
                  >
                    Purchase Credits
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <Button variant='secondary' onClick={onClose}>
            Maybe Later
          </Button>
        </div>
      </div>
    </Modal>
  );
};
export default UpgradeModal;
